import { inject, injectable } from 'inversify';
import { catchError, retry, take, timeout } from 'rxjs/operators';
import { isTerminalResolveStatus } from '../utils/resolve-retry.utils';

import { firstValueFrom, fromEvent, Observable, of, throwError, timer } from 'rxjs';
import { ConnectivityUtils } from '../utils/connectivity.utils';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../models/google-drive-file';
import { Logger } from '../models/logger';
import { GAContants } from '../types/ga.types';
import { OperationType, OperationTypes, ProcessorMetadata } from '../types/types';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { getFileMimeType } from '../utils/file';
import { NetworkError, RequestUtils } from '../utils/request.utils';
import { UrlUtils } from '../utils/url.utils';
import { GAService } from './analytics.service';
import { ApiConfig } from './api-config';
import { ConnectivityService } from './connectivity.service';
import { EnforcementErrorService } from './enforcement-error.service';
import { EnvironmentService } from './environment.service';
import { LightHttp } from './light-http.service';
import { SettingsService } from './settings.service';
import { WebSocketService } from './websocket.service';


@injectable()
export class ProcessorResolverService {
    private readonly logger = Logger.getLogger('ProcessorResolverService');

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(GAService) private gaService: GAService,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(EnvironmentService) private env: EnvironmentService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(ConnectivityService) private connectivityService: ConnectivityService,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService,
    ) {
    }

    private getDomainToBeUsed() {
        try {
            let useDomain = null;
            let useDomainValidator = null;
            if (BrowserUtils.hasWindow()) {
                useDomain = UrlUtils.getParameterByName(window.location.href, 'useDomain') || window['useDomain'] || null;
                useDomainValidator = UrlUtils.getParameterByName(window.location.href, 'useDomainValidator') || window['useDomainValidator'] || null;
            }
            if (!useDomain) {
                return {};
            }
            return {
                useDomain,
                useDomainValidator,
            };
        } catch (e) {
            return {};
        }
    }

    async generateFileUploadRequestMetadata(
        file: File | GoogleDriveFile,
        fromType: string[],
        toType: string,
        operationType: OperationType,
        resolveOptions?: { suppressGA?: boolean, excludeDomains?: string[] },
    ): Promise<FileUploadRequestMetadata> {
        await this.wsService.ensureWsDomain();
        if (!this.wsService.getDomain()) {
            this.logger.warn({ message: 'upload.resolve.wsDomainEmpty', data: { wsId: this.wsService.getId() } });
        }
        await firstValueFrom(this.settingsService.userSettings.pipe(take(1), timeout(5000), catchError(() => of(null))));

        let test = 'false';
        if (BrowserUtils.hasWindow()) {
            test = UrlUtils.getParameterByName(window.location.href, 'test') || 'false';
        }
        const { useDomain, useDomainValidator } = this.getDomainToBeUsed();

        if (!resolveOptions?.suppressGA) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-START'],
                GAContants.EventLabel.CONVERT,
                `${file.name}-${file.size}`,
            );
        }
        try {
            const options = {
                params: {
                    from: fromType,
                    to: toType,
                    operation: operationType,
                    contentNames: file.name,
                    contentTypes: getFileMimeType(file),
                    contentSizes: file.size + '',
                    test,
                    wsId: this.wsService.getId(),
                    wsDomain: this.wsService.getDomain(),
                    requireActiveDownload: (file instanceof File || file instanceof Blob) ? 'false' : 'true',
                    ...(useDomain ? { useDomain, useDomainValidator } : null),
                    // upload-skip-edge (QA fu-r3 T9): the failed edges are matched by host inside `getEdgesRandomized`. The old
                    // `.filter(d => !excludeDomains.includes(d))` compared `https://ncf-…` with bare `d` and never skipped one.
                    ...(operationType === OperationTypes.UPLOAD ? { preferedDomains: this.connectivityService.getEdgesRandomized(4, resolveOptions?.excludeDomains).map(edge => edge.domain) } : {}),
                },
            };

            // this.http.get<ProcessorMetadata[]>(`${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`, options)
            //         .pipe(
            //             retry(10),
            //         ).subscribe({
            //             error: (e) => {
            //                 console.log(e)
            //             }
            //         })

            let request = RequestUtils.getSuccessValueOrThrow$<ProcessorMetadata[]>(
                [
                    this.http.get<ProcessorMetadata[]>(`${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`, options)

                ],
                8000
            ).pipe(
                retry({
                    count: 5,
                    delay: (error, retryCount) => {
                        // 2026-09-07: a verdict-type 4xx (406 "file type is not supported",
                        // 400, 413, 415 …) does not change on retry; the ladder used to spend
                        // ~31 s re-asking before showing the same answer. 408/429 still retry.
                        if (isTerminalResolveStatus((error as { status?: number })?.status)) {
                            return throwError(() => error);
                        }
                        // Enforcement / billing rejections (HTTP 429 enforcement_error,
                        // HTTP 402 insufficient_credits) MUST short-circuit the retry
                        // loop. Pre-Phase-7 we retried 5× with exponential backoff,
                        // which hid the rejection from the user — Convert button hung
                        // in "Loading…" forever (runbook bug 6a-B1, P0).
                        if (EnforcementErrorService.isEnforcementError(error)) {
                            this.enforcementErrorService.show(
                                EnforcementErrorService.extractDetails(error),
                            );
                            return throwError(() => error);
                        }
                        if (typeof navigator !== 'undefined' && !navigator.onLine) {
                            return fromEvent(window, 'online').pipe(
                                take(1),
                                timeout(30000),
                            );
                        }
                        return timer(Math.min(1000 * Math.pow(2, retryCount - 1), 16000));
                    },
                }),
            );
            const data: ProcessorMetadata[] = await firstValueFrom(request);
            if (!resolveOptions?.suppressGA) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['NETWORK'],
                    GAContants.EventAction['FILE-PROCESSOR-RESOLVE-END'],
                    GAContants.EventLabel.CONVERT,
                    `${file.name}-${file.size}`,
                );
            }
            return new FileUploadRequestMetadata(data, file);
        } catch (e) {
            if (!resolveOptions?.suppressGA) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['NETWORK'],
                    GAContants.EventAction['FILE-PROCESSOR-RESOLVE-FAILED'],
                    GAContants.EventLabel.CONVERT,
                    `${file.name}-${file.size}`,
                );
            }
            console.error(e);
            // Preserve enforcement-rejection context so the caller (and any
            // outer retry layer) can recognise it via
            // EnforcementErrorService.isEnforcementError(...) and bail
            // instead of wrapping it as a generic resolve failure.
            if (EnforcementErrorService.isEnforcementError(e)) {
                throw e;
            }
            if (!ConnectivityUtils.isOnline()) {
                throw new NetworkError('You appear to be offline. Please check your connection and try again.');
            }
            const errorSuffix = ' Error was thrown while trying to resolve edge servers.';
            if (e instanceof NetworkError) {
                throw new NetworkError(e.message + errorSuffix);
            } else {
                throw new Error(e.message + errorSuffix);
            }
        }
    }

    async generateFileUploadRequestMetadataForGenericDataProcessing(): Promise<FileUploadRequestMetadata> {
        let test = 'false';
        if (BrowserUtils.hasWindow()) {
            test = UrlUtils.getParameterByName(window.location.href, 'test') || 'false';
        }
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory['NETWORK'],
            GAContants.EventAction['FILE-PROCESSOR-RESOLVE-START'],
            GAContants.EventLabel.CONVERT,
            `generic-generic`,
        );
        try {
            const options = {
                params: {
                    from: 'generic',
                    to: 'generic',
                    operation: OperationTypes.PROCESS,
                    contentNames: 'generic.txt',
                    contentTypes: 'text/plain',
                    contentSizes: '0',
                    test,
                    wsId: this.wsService.getId(),
                    wsDomain: this.wsService.getDomain(),
                    requireActiveDownload: 'false',
                },
            };

            const data: ProcessorMetadata[] = await RequestUtils.getSuccessValueOrThrow<ProcessorMetadata[]>(
                this.http.get<ProcessorMetadata[]>(
                    `${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`,
                    options
                ),
            );

            this.logger.trace1({
                message: 'ProcessMetadata',
                data: JSON.stringify(data),
            });

            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-END'],
                GAContants.EventLabel.CONVERT,
                `generic-generic`,
            );
            return new FileUploadRequestMetadata(data, null);
        } catch (e) {
            this.logger.error({
                message: e.message || 'Error getting process metadata',
                error: e,
            });
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-FAILED'],
                GAContants.EventLabel.CONVERT,
                `generic-generic`,
            );
        }
    }
}
